import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import { InputComponent } from "../../shared/component/inputs/input-component/input.component";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { RegExpService } from "../../../util/common/reg-exp.service";
import { CheckboxComponent } from "../../shared/component/checkbox/checkbox.component";
import { ButtonComponent } from "../../shared/component/buttons/button.component";
import { NOTIFICATIONS, NotificationsService } from "../../../core/service/notifications.service";
import { UserBackendService } from "../../user/service/UserBackendService";
import { UserCompleteRegistrationDTO } from "../../user/dto/UserCompleteRegistrationDTO";
import { firstValueFrom,  Subject } from "rxjs";
import { Router } from "@angular/router";
import { WEBSITE_PROD_ORCA_PRIVACY_POLICY, WEBSITE_PROD_ORCA_TERMS_OF_USE } from "../../../util/common/links";
import { FRONT_URLS } from "../../../util/common/url-parts";
import { OrcaLogoComponent } from "../../../core/component/orca-logo-svg/orca-logo-svg.component";
import { ShowUIService } from "../../../core/service/show-ui.service";
import { takeUntil } from "rxjs/operators";
import { CALLING_CODE_KEY_IN_STORAGE } from "../../../util/common/countries";
import { EntranceService } from "../../entrance/service/entrance.service";

@Component({
  selector: 'app-new-user',
  imports: [
    InputComponent,
    ReactiveFormsModule,
    CheckboxComponent,
    ButtonComponent,
    OrcaLogoComponent,
  ],
  templateUrl: './new-user.component.html',
  styleUrls: [
    './new-user.component.scss',
    '../entrance-group.scss',
  ],
  standalone: true,
})
export class NewUserComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationsService);
  private userBackendService = inject(UserBackendService);
  private router = inject(Router);
  private showUIService = inject(ShowUIService);
  private entranceService = inject(EntranceService);
  private _destroyStream = new Subject<void>();

  protected readonly WEBSITE_PROD_ORCA_TERMS_OF_USE = WEBSITE_PROD_ORCA_TERMS_OF_USE;
  protected readonly WEBSITE_PROD_ORCA_PRIVACY_POLICY = WEBSITE_PROD_ORCA_PRIVACY_POLICY;

  newUserForm = new FormGroup({
    name: new FormControl<string>('', { validators: [Validators.required] }),
    company: new FormControl<string>('', { validators: [Validators.required] }),
    position: new FormControl<string>('', { validators: [Validators.required] }),
    countryCode: new FormControl<string>('', {
      validators: [
        Validators.required,
        Validators.pattern(/^\d{1,4}$/),
      ]
    }),
    phone: new FormControl<string>('', {
      validators: [
        Validators.required,
        Validators.pattern(/^\d{1,15}$/),
      ]
    }),
    agreements: new FormControl<boolean>(false, { validators: [Validators.requiredTrue] }),
    marketing: new FormControl<boolean>(false),
  });

  constructor() {
    this.showUIService.setShowHorNavBar(false);
    this.showUIService.setShowSideNavBar(false);
  }

  ngOnInit() {
    this.newUserForm.controls.phone.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
      if (this.newUserForm.controls.phone.touched) {
        this.newUserForm.controls.phone.markAsUntouched();
      }
    });
    this.newUserForm.controls.countryCode.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
        if (this.newUserForm.controls.countryCode.touched) {
          this.newUserForm.controls.countryCode.markAsUntouched();
        }
      });
    this.loadCountryCode();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async continue(): Promise<void> {
    if (this.newUserForm.invalid) {
      this.notificationService.error(NOTIFICATIONS.EMPTY_FIELDS);
      return;
    }

    const data: UserCompleteRegistrationDTO = {
      name: this.newUserForm.value.name,
      company: this.newUserForm.value.company,
      position: this.newUserForm.value.position,
      phone: this.formatPhoneNumber(),
      isMarketingAccepted: this.newUserForm.value.marketing,
      isTermsAndConditionsAccepted: this.newUserForm.value.agreements,
    };
    await firstValueFrom(this.userBackendService.completeRegistration(data));
    this.entranceService.setCompanyName(this.newUserForm.value.company);
    await this.router.navigateByUrl(FRONT_URLS.ENTRANCE, { replaceUrl: true });
  }

  private loadCountryCode(): void {
    const storedCode = localStorage.getItem(CALLING_CODE_KEY_IN_STORAGE);

    this.newUserForm.patchValue({
      countryCode: storedCode && storedCode !== 'null' && storedCode !== '' ? storedCode : '1'
    });
  }


  private formatPhoneNumber(): string {
    const countryCode = this.newUserForm.value.countryCode;
    const phoneNumber = this.newUserForm.value.phone;

    if (phoneNumber?.startsWith('+')) {
      return phoneNumber;
    }

    if (countryCode) {
      return `+${countryCode}${phoneNumber}`;
    }

    return phoneNumber;
  }
}
